import { getProgramIdentifier, getProgramIdFromUrl } from "../utils"
import { RootState } from "./rootReducer"
import { VerifyResponse } from '../components/success/SuccessStage'

type PersistedState = {
  timestamp: number
  state: RootState
}

const PERSIST_KEY = `reduxState_${getProgramIdentifier()}`
const MAX_AGE_MS = 60 * 60 * 1000 // one hour in ms

export const loadState = (): RootState | undefined => {
    try {
        const serialized = localStorage.getItem(PERSIST_KEY)
        if (serialized === null) {
          return undefined
        }

        const parsed = JSON.parse(serialized) as PersistedState | RootState

        const persisted: PersistedState = ('timestamp' in parsed && 'state' in parsed)
          ? parsed as PersistedState
          : { timestamp: Date.now(), state: parsed as RootState }

        if (Date.now() - persisted.timestamp > MAX_AGE_MS) {
          deleteState()
          return undefined
        }

        const parsedSerializedState = persisted.state

        //sanity check if program_id matches the saved id
        if(getProgramIdFromUrl() !== parsedSerializedState?.program.data.id) {
            return undefined
        }

        const participantHasData = Object.keys(parsedSerializedState.participant.data).length > 0
        const hasRegistrations = parsedSerializedState.registrations.list.length > 0
        if( participantHasData || hasRegistrations) {
            parsedSerializedState.misc.isRestoredData = true
        }else {
            parsedSerializedState.misc.isRestoredData = false
        }

        // Always reset successfulResponse when loading state to prevent old responses from persisting
        parsedSerializedState.misc.successfulResponse = {} as VerifyResponse

        return parsedSerializedState
    } catch (err) {
        console.error('Could not load state', err)
        return undefined
    }
}

export const saveState = (state: RootState): void => {
    try {
        const payload: PersistedState = {
          timestamp: Date.now(),
          state
        }
        const serialized = JSON.stringify(payload)
        localStorage.setItem(PERSIST_KEY, serialized)
    } catch (err) {
        console.error('Could not save state', err)
    }
}

export const deleteState = (): void => {
    localStorage.removeItem(PERSIST_KEY)
    localStorage.removeItem(`lastVisitedRoute_${getProgramIdentifier()}`)
    localStorage.removeItem(LIVE_MESSAGES_KEY)
}

export const deleteLastVisitedRoute = (): void => {
    localStorage.removeItem(`lastVisitedRoute_${getProgramIdentifier()}`)
}

const LIVE_MESSAGES_KEY = `acknowledgedLiveMessages_${getProgramIdentifier()}`

export const loadAcknowledgedLiveMessages = (): string[] => {
    try {
        const raw = localStorage.getItem(LIVE_MESSAGES_KEY)
        if (raw) {
            return JSON.parse(raw)
        }
    } catch (e) {
        console.error('Could not load acknowledged live messages', e)
    }
    return []
}

export const saveAcknowledgedLiveMessages = (messages: string[]): void => {
    try {
        localStorage.setItem(LIVE_MESSAGES_KEY, JSON.stringify(messages))
    } catch (e) {
        console.error('Could not save acknowledged live messages', e)
    }
}